import { ReactNode } from 'react';
import { Plus, X } from 'lucide-react';

// ── InitialAvatar ─────────────────────────────────────────────────────────────

interface InitialAvatarProps {
    name: string;
    size?: 'sm' | 'md' | 'lg';
}

const avatarSizes = {
    sm: 'w-8 h-8 text-base',
    md: 'w-12 h-12 text-xl',
    lg: 'w-16 h-16 text-2xl',
};

/**
 * DS: Orange circle avatar showing the first letter of a name.
 * Matches the persona avatar style in PersonaList.
 */
export function InitialAvatar({ name, size = 'md' }: InitialAvatarProps) {
    return (
        <div className={`${avatarSizes[size]} bg-orange-100 rounded-full flex items-center justify-center border-2 border-black shrink-0`}>
            <span className="font-black text-orange-500">
                {name.charAt(0).toUpperCase()}
            </span>
        </div>
    );
}

// ── MonoLabel ─────────────────────────────────────────────────────────────────

interface MonoLabelProps {
    children: ReactNode;
}

/**
 * DS: Tiny uppercase monospace label — used as a field/section micro-label.
 */
export function MonoLabel({ children }: MonoLabelProps) {
    return (
        <span className="text-xs font-mono uppercase text-gray-400">{children}</span>
    );
}

// ── TextLink ──────────────────────────────────────────────────────────────────

interface TextLinkProps {
    onClick: () => void;
    children: ReactNode;
}

/**
 * DS: Borderless inline text link — underline-b-2, orange hover.
 * Used for "View Details →" style actions.
 */
export function TextLink({ onClick, children }: TextLinkProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="text-xs font-mono font-bold uppercase border-b-2 border-black hover:text-orange-500 transition-colors"
        >
            {children}
        </button>
    );
}

// ── AddItemButton ─────────────────────────────────────────────────────────────

interface AddItemButtonProps {
    onClick: () => void;
    label?: string;
}

/**
 * DS: Small orange "＋ Add" button used in list sections (goals, frustrations).
 */
export function AddItemButton({ onClick, label = 'Add' }: AddItemButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center gap-1 text-xs font-bold uppercase text-orange-500 hover:text-orange-600"
        >
            <Plus size={14} /> {label}
        </button>
    );
}

// ── RemoveItemButton ──────────────────────────────────────────────────────────

interface RemoveItemButtonProps {
    onClick: () => void;
    'aria-label'?: string;
}

/**
 * DS: Small × remove button used beside list inputs.
 * Gray border → red on hover.
 */
export function RemoveItemButton({ onClick, 'aria-label': ariaLabel }: RemoveItemButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={ariaLabel ?? 'Remove item'}
            className="px-3 border-2 border-gray-300 text-gray-400 hover:border-red-500 hover:text-red-500"
        >
            <X size={16} />
        </button>
    );
}

// ── SubmitButton ──────────────────────────────────────────────────────────────

interface SubmitButtonProps {
    disabled?: boolean;
    loading?: boolean;
    loadingLabel?: string;
    children: ReactNode;
}

/**
 * DS: Black full-width-on-mobile submit button with orange brutal shadow.
 * Matches the save button in PersonaDetail.
 */
export function SubmitButton({ disabled, loading, loadingLabel = 'Saving...', children }: SubmitButtonProps) {
    return (
        <button
            type="submit"
            disabled={disabled || loading}
            className="w-full md:w-auto flex justify-center items-center gap-2 px-6 py-4 md:py-3 bg-black text-white font-bold uppercase text-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-brutal-orange hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-brutal-orange-hover active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all"
        >
            {loading ? loadingLabel : children}
        </button>
    );
}
